import React, { useState } from 'react';
import ReadAloudIcon from '../../Components/ReadAloudIcon';
import TextToSpeech from '../../Components/TextToSpeech';
import { AudioPlayerState, useAudio } from '../../Context/AudioContext';
import { Question } from '../../Types/Question';
import { QuestionResponse } from '../../Types/QuestionResponse';

interface QuizQuestionOptionProps {
  index: number;
  option: string;
  currentQuestion: Question;
  currentQuestionResponse: QuestionResponse;
  handleOptionClick: (index: number) => void;
}

const QuizQuestionOption: React.FC<QuizQuestionOptionProps> = ({
  index,
  option,
  currentQuestion,
  currentQuestionResponse,
  handleOptionClick,
}) => {
  const { audioManager } = useAudio();
  const [optionPlaybackState, setOptionPlaybackState] =
    useState<AudioPlayerState>(AudioPlayerState.Stop);
  const [explanationPlaybackState, setExplanationPlaybackState] =
    useState<AudioPlayerState>(AudioPlayerState.Stop);
  const [isOptionPlaybackLoaded, setIsOptionPlaybackLoaded] = useState(false);
  const [isExplanationPlaybackLoaded, setIsExplanationPlaybackLoaded] =
    useState(false);

  const isChosenOption = currentQuestionResponse?.chosenOptionIndex === index;
  const isCorrectOption =
    currentQuestion.correctOptionIndex === index &&
    currentQuestionResponse?.answerState !== 'not answered';

  let optionClasses = 'border-gray-50 hover:border-blue-400';
  let letterClasses = 'text-gray-400 bg-gray-200';

  if (isChosenOption) {
    switch (currentQuestionResponse?.answerState) {
      case 'not answered':
        optionClasses =
          'border-[color:var(--informational-primary,#0D6EFD)] bg-blue-600 bg-opacity-10 ';
        letterClasses = 'bg-blue-600 text-white';
        break;
      case 'incorrect':
        optionClasses =
          'border-[color:var(--informational-error,#DC3545)] bg-red-500 bg-opacity-10 ';
        letterClasses = 'bg-red-500 text-white';
        break;
      case 'correct':
        optionClasses =
          'border-[color:var(--informational-success,#198754)] bg-green-700 bg-opacity-10 ';
        letterClasses = 'bg-green-700 text-white';
        break;
    }
  } else if (isCorrectOption) {
    optionClasses =
      'border-[color:var(--informational-success,#198754)] bg-green-700 bg-opacity-10 ';
    letterClasses = 'bg-green-700 text-white';
  }

  return (
    <div
      data-testid={`quiz-question-option-${index}`}
      className={`justify-center items-stretch self-stretch flex flex-col max-md:max-w-full cursor-pointer mt-3 p-2 border-2 border-solid rounded-lg ${optionClasses}`}
      onClick={() => handleOptionClick(index)}
    >
      <div
        className={`items-stretch self-stretch flex justify-between gap-2 mt-1 xl:p-2 max-md:max-w-full sm:max-md:flex-wrap`}
      >
        <div
          className={`flex justify-center items-center text-base font-bold leading-6 whitespace-nowrap justify-center items-center aspect-square h-6 px-2 rounded-3xl ${letterClasses}`}
        >
          {String.fromCharCode(65 + index)}
        </div>
        <div className="flex-grow text-base leading-6">{option}</div>
        <ReadAloudIcon
          playbackState={optionPlaybackState}
          isPlaybackLoaded={isOptionPlaybackLoaded}
          onPlayClick={() => {
            setOptionPlaybackState(AudioPlayerState.Play);
            setIsOptionPlaybackLoaded(false);
          }}
          onStopClick={() => setOptionPlaybackState(AudioPlayerState.Stop)}
        />
      </div>
      <TextToSpeech
        key={`quiz-question-${currentQuestion.id}-option-${index}`}
        descriptorUrl={`${window.env.REACT_APP_API_URL}/tts/quiz/option/${currentQuestion.id}/${index}`}
        manager={audioManager}
        state={optionPlaybackState}
        preload={false}
        analyticsContext={{
          type: 'quiz_option',
          questionId: currentQuestion.id,
          optionId: index.toString(),
        }}
        onEnd={() => setOptionPlaybackState(AudioPlayerState.Stop)}
        onPlay={() => setIsOptionPlaybackLoaded(true)}
        onPause={() => setOptionPlaybackState(AudioPlayerState.Stop)}
      />

      {/* This section displays guiding question explanations */}
      {currentQuestionResponse?.answerState !== 'not answered' &&
        currentQuestion.correctOptionIndex === index &&
        currentQuestion.correctAnswerExplanation != null && (
          <div
            className="text-neutral-800 text-opacity-70 text-sm mt-2 max-md:max-w-full"
            data-testid="correct-guiding-answer-explanation"
          >
            <div className="flex gap-2">
              <div className="flex-1">
                {currentQuestion.correctAnswerExplanation}
              </div>
              {currentQuestion.correctAnswerExplanation && (
                <ReadAloudIcon
                  playbackState={explanationPlaybackState}
                  isPlaybackLoaded={isExplanationPlaybackLoaded}
                  onPlayClick={() => {
                    setExplanationPlaybackState(AudioPlayerState.Play);
                    setIsExplanationPlaybackLoaded(false);
                  }}
                  onStopClick={() =>
                    setExplanationPlaybackState(AudioPlayerState.Stop)
                  }
                  buttonClassName="mr-2 p-0.5 rounded cursor-pointer"
                />
              )}
            </div>
          </div>
        )}
      {/* This section displays assessment question explanations */}
      {currentQuestionResponse?.answerState !== 'not answered' &&
        currentQuestionResponse.chosenOptionIndex === index &&
        currentQuestion.questionOptionExplanations != null &&
        currentQuestion.questionOptionExplanations.length > index && (
          <div
            className="text-neutral-800 text-opacity-70 text-sm mt-2 max-md:max-w-full"
            data-testid="correct-assessment-answer-explanation"
          >
            <div className="flex gap-2">
              <div className="flex-1">
                {
                  currentQuestion.questionOptionExplanations[
                    currentQuestionResponse.chosenOptionIndex
                  ]
                }
              </div>
              {currentQuestion.questionOptionExplanations[
                currentQuestionResponse.chosenOptionIndex
              ] && (
                <ReadAloudIcon
                  playbackState={explanationPlaybackState}
                  isPlaybackLoaded={isExplanationPlaybackLoaded}
                  onPlayClick={() => {
                    setExplanationPlaybackState(AudioPlayerState.Play);
                    setIsExplanationPlaybackLoaded(false);
                  }}
                  onStopClick={() =>
                    setExplanationPlaybackState(AudioPlayerState.Stop)
                  }
                  buttonClassName="mr-2 p-0.5 rounded cursor-pointer"
                />
              )}
            </div>
          </div>
        )}

      {currentQuestion?.id ? (
        <TextToSpeech
          key={`quiz-question-${currentQuestion.id}-option-${index}-explanation`}
          descriptorUrl={`${window.env.REACT_APP_API_URL}/tts/quiz/explanation/${currentQuestion.id}/${index}`}
          manager={audioManager}
          state={explanationPlaybackState}
          preload={false}
          analyticsContext={{
            type: 'quiz_explanation',
            questionId: currentQuestion.id,
            optionId: index.toString(),
          }}
          onEnd={() => setExplanationPlaybackState(AudioPlayerState.Stop)}
          onPlay={() => setIsExplanationPlaybackLoaded(true)}
          onPause={() => setExplanationPlaybackState(AudioPlayerState.Stop)}
        />
      ) : null}
    </div>
  );
};

export default QuizQuestionOption;
